import React, { useCallback, useMemo, useRef, useState } from 'react'
import './SplitView.css'

type Props = {
  left: React.ReactNode
  right: React.ReactNode
  initialLeftWidth?: number // px
  minLeftWidth?: number // px
  maxLeftWidth?: number // px
  onChangeLeftWidth?: (leftWidth: number) => void
}

export function SplitView({ left, right, initialLeftWidth = 320, minLeftWidth = 220, maxLeftWidth = 700, onChangeLeftWidth }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [leftWidth, setLeftWidth] = useState<number>(initialLeftWidth)

  const clamp = useCallback((value: number) => Math.max(minLeftWidth, Math.min(maxLeftWidth, value)), [minLeftWidth, maxLeftWidth])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Kun venstre klik/primær
      if (e.button !== 0) return

      const container = containerRef.current
      if (!container) return

      // Capture så vi stadig får move selvom musen forlader handle
      e.currentTarget.setPointerCapture(e.pointerId)

      const rect = container.getBoundingClientRect()

      const onMove = (ev: PointerEvent) => {
        const next = clamp(ev.clientX - rect.left)
        setLeftWidth(next)
        onChangeLeftWidth?.(next)
      }

      const onUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        try {
          // release hvis muligt
          ;(e.currentTarget as HTMLDivElement).releasePointerCapture(ev.pointerId)
        } catch {}
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    },
    [clamp, onChangeLeftWidth],
  )

  return (
    <div ref={containerRef} className="split-root">
      <div className="split-left" style={{ width: leftWidth }}>
        {left}
      </div>

      <div className="split-resizer" onPointerDown={onPointerDown} />

      <div className="split-right">{right}</div>
    </div>
  )
}
